import React, { useEffect, useState, useContext } from "react";
import {
	TextField,
	Checkbox,
	Button,
	Box,
	Typography,
	FormControlLabel,
	Alert,
	Snackbar,
	styled,
	Chip,
	IconButton,
	Paper,
	Tabs,
	Tab,
	Badge,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { AuthContext } from "context";
import MDAlert from "../../components/MDAlert";
import MDBox from "../../components/MDBox";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/it";

dayjs.extend(customParseFormat);
dayjs.locale("it");

const VisuallyHiddenInput = styled("input")({
	clip: "rect(0 0 0 0)",
	clipPath: "inset(50%)",
	height: 1,
	overflow: "hidden",
	position: "absolute",
	bottom: 0,
	left: 0,
	whiteSpace: "nowrap",
	width: 1,
});

const StatusChip = styled(Chip)(({ theme, status }) => ({
	fontWeight: "bold",
	...(status === "toApprove" && {
		backgroundColor: theme.palette.warning.light,
		color: theme.palette.warning.contrastText,
	}),
	...(status === "rejectedDone" && {
		backgroundColor: theme.palette.info.light,
		color: theme.palette.info.contrastText,
	}),
	...(status === "approved" && {
		backgroundColor: theme.palette.success.light,
		color: theme.palette.success.contrastText,
	}),
	...(status === "rejected" && {
		backgroundColor: theme.palette.error.light,
		color: theme.palette.error.contrastText,
	}),
	...(status === "expired" && {
		backgroundColor: theme.palette.error.light,
		color: theme.palette.error.contrastText,
	}),
}));

const filesContent = {};

const UploadComponent = ({ documents, setDocuments, onSave }) => {
	const authContext = useContext(AuthContext);
	const [errors, setErrors] = useState({});
	const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
	const [viewingInfo, setViewingInfo] = useState(null);
	const [activeTab, setActiveTab] = useState(0);

	const handleFileChange = (docId, event) => {
		if (!event.target.files) return;

		const filePromise = new Promise((resolve) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				const fileObj = {
					name: event.target.files[0].name,
					type: event.target.files[0].type,
					size: event.target.files[0].size,
					modified: event.target.files[0].lastModified,
					//imageData: event.target.files[0],
					content: null,
				};
				const contentObj = reader.result;

				filesContent[docId] = contentObj;

				resolve(fileObj);
			};
			reader.readAsDataURL(event.target.files[0]);
		});

		Promise.resolve(filePromise).then((processedFile) => {
			setDocuments((prevDocs) =>
				prevDocs.map((doc) => (doc.id === docId ? { ...doc, file: processedFile } : doc))
			);
		});
	};

	const handleAdditionalInfoChange = (docId, infoId, value) => {
		setDocuments((prevDocs) =>
			prevDocs.map((doc) =>
				doc.id === docId
					? {
							...doc,
							additionalInformation: doc.additionalInformation.map((info) =>
								info.id === infoId ? { ...info, value } : info
							),
						}
					: doc
			)
		);
	};

	const handleDeleteFile = (docId) => {
		setDocuments((prevDocs) =>
			prevDocs.map((doc) => (doc.id === docId ? { ...doc, file: null, status: "toUpload" } : doc))
		);
	};

	const validateDocuments = (docs) => {
		const newErrors = {};

		docs.forEach((doc) => {
			if (doc.required && doc.status === "toUpload" && !doc.file) {
				newErrors[doc.id] = newErrors[doc.id] || {};
				newErrors[doc.id].file = "File richiesto";
			}

			if (doc.status === "toUpload" || doc.status === "rejected" || doc.status === "expired") {
				doc.additionalInformation.forEach((info) => {
					if (doc.file && info.required && !info.value) {
						newErrors[doc.id] = newErrors[doc.id] || {};
						newErrors[doc.id][info.id] = `Campo richiesto`;
					}
				});
			}
		});

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	useEffect(() => {
		console.log("Errors:", errors);
	}, [errors]);

	const handleSave = () => {
		let docsToValidate;
		switch (activeTab) {
			case 0:
				return;
				break;
			case 1:
				docsToValidate = documents.filter((doc) => doc.status === "rejected");
				break;
			case 2:
				docsToValidate = documents.filter((doc) => doc.status === "toUpload");
				break;
			case 3:
				docsToValidate = documents.filter((doc) => doc.status === "expired");
				break;
			default:
				docsToValidate = [];
		}

		if (validateDocuments(docsToValidate)) {
			if (activeTab === 2) {
				docsToValidate = docsToValidate.filter((doc) => doc.file);
			}

			for (let i = 0; i < docsToValidate.length; i++) {
				if (docsToValidate[i].file) docsToValidate[i]["file"]["content"] = filesContent[docsToValidate[i].id];
			}
			onSave(docsToValidate);
		} else {
			setShowErrorSnackbar(true);
		}
	};

	const handleDownload = (url) => {
		window.open(url, "_blank");
	};

	const renderDocumentContent = (doc) => {
		switch (doc.status) {
			case "toUpload":
				return (
					<>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: 2 }}>
							<Button
								component="label"
								variant="outlined"
								startIcon={<CloudUploadIcon />}
								sx={{
									maxWidth: 200,
									textOverflow: "ellipsis",
									overflow: "hidden",
									whiteSpace: "nowrap",
									fontSize: "0.875rem",
									backgroundColor: "white",
									borderColor: "black!important",
									boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
									color: "black!important",
									transition: "box-shadow 0.3s ease-in-out",
									"&:hover, &:focus, &:active": {
										backgroundColor: "white",
										boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
										borderColor: "black!important",
										color: "black!important",
									},
								}}
							>
								Carica file
								<VisuallyHiddenInput
									type="file"
									onChange={(event) => handleFileChange(doc.id, event)}
								/>
							</Button>
							{doc.file && (
								<IconButton
									size="small"
									onClick={() => handleDeleteFile(doc.id)}
									sx={{ color: "error.main" }}
								>
									<CloseIcon />
								</IconButton>
							)}
						</Box>
						{errors[doc.id]?.file && (
							<Typography color="error" variant="caption">
								{errors[doc.id].file}
							</Typography>
						)}
						{doc.file && (
							<Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
								File selezionato: {doc.file.name}
							</Typography>
						)}
						{renderAdditionalInfo(doc, true)}
					</>
				);
			case "rejectedDone":
			case "toApprove":
			case "approved":
				return (
					<>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
							<Button
								variant="outlined"
								startIcon={<DownloadIcon />}
								onClick={() => handleDownload(doc.url)}
								sx={{
									maxWidth: 200,
									textOverflow: "ellipsis",
									overflow: "hidden",
									whiteSpace: "nowrap",
									fontSize: "0.875rem",
									backgroundColor: "white",
									borderColor: "black!important",
									boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
									color: "black!important",
									transition: "box-shadow 0.3s ease-in-out",
									"&:hover, &:focus, &:active": {
										backgroundColor: "white",
										boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
										borderColor: "black!important",
										color: "black!important",
									},
								}}
							>
								Scarica
							</Button>
							<StatusChip
								label={
									doc.status === "approved"
										? "Approvato"
										: doc.status === "toApprove"
											? "In attesa"
											: doc.status === "rejectedDone"
												? "Rettificato"
												: "Da rettificare"
								}
								status={doc.status}
							/>
						</Box>
					</>
				);
			case "rejected":
				return (
					<>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
							{!doc?.file && (
								<Button
									variant="outlined"
									startIcon={<DownloadIcon />}
									onClick={() => handleDownload(doc.url)}
									sx={{
										maxWidth: 200,
										textOverflow: "ellipsis",
										overflow: "hidden",
										whiteSpace: "nowrap",
										fontSize: "0.875rem",
										backgroundColor: "white",
										borderColor: "black!important",
										boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
										color: "black!important",
										transition: "box-shadow 0.3s ease-in-out",
										"&:hover, &:focus, &:active": {
											backgroundColor: "white",
											boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
											borderColor: "black!important",
											color: "black!important",
										},
									}}
								>
									Scarica
								</Button>
							)}
							<StatusChip label={"Necessita rettifica"} status={doc.status} />
						</Box>
						{doc.rejectionReason && (
							<Typography color="error" variant="body2" sx={{ marginBottom: 2 }}>
								Motivo: {doc.rejectionReason}
							</Typography>
						)}
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
							<Button
								component="label"
								variant="outlined"
								startIcon={<CloudUploadIcon />}
								sx={{
									maxWidth: 200,
									textOverflow: "ellipsis",
									overflow: "hidden",
									whiteSpace: "nowrap",
									fontSize: "0.875rem",
									backgroundColor: "white",
									borderColor: "black!important",
									boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
									color: "black!important",
									transition: "box-shadow 0.3s ease-in-out",
									"&:hover, &:focus, &:active": {
										backgroundColor: "white",
										boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
										borderColor: "black!important",
										color: "black!important",
									},
								}}
							>
								Carica Nuovo File
								<VisuallyHiddenInput
									type="file"
									onChange={(event) => {
										if (
											window.confirm(
												"Sei sicuro di voler caricare un nuovo file, l'operazione cancellerà irreversibilmente il file vecchio?"
											)
										)
											handleFileChange(doc.id, event);
									}}
								/>
							</Button>
						</Box>
						{doc.file && (
							<Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
								File selezionato: {doc.file.name}
							</Typography>
						)}

						{renderAdditionalInfo(doc, true)}
					</>
				);
			case "expired":
				return (
					<>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
							{doc.url && (
								<Button
									variant="outlined"
									startIcon={<DownloadIcon />}
									onClick={() => handleDownload(doc.url)}
									sx={{
										maxWidth: 200,
										textOverflow: "ellipsis",
										overflow: "hidden",
										whiteSpace: "nowrap",
										fontSize: "0.875rem",
										backgroundColor: "white",
										borderColor: "black!important",
										boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
										color: "black!important",
										transition: "box-shadow 0.3s ease-in-out",
										"&:hover, &:focus, &:active": {
											backgroundColor: "white",
											boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
											borderColor: "black!important",
											color: "black!important",
										},
									}}
								>
									Scarica
								</Button>
							)}
							<StatusChip label={"Scaduto"} status={doc.status} />
						</Box>
						<Typography color="error" variant="body2" sx={{ marginBottom: 2 }}>
							Il documento è scaduto. Carica un nuovo documento aggiornato.
						</Typography>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
							<Button
								component="label"
								variant="outlined"
								startIcon={<CloudUploadIcon />}
								sx={{
									maxWidth: 200,
									textOverflow: "ellipsis",
									overflow: "hidden",
									whiteSpace: "nowrap",
									fontSize: "0.875rem",
									backgroundColor: "white",
									borderColor: "black!important",
									boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
									color: "black!important",
									transition: "box-shadow 0.3s ease-in-out",
									"&:hover, &:focus, &:active": {
										backgroundColor: "white",
										boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)",
										borderColor: "black!important",
										color: "black!important",
									},
								}}
							>
								Carica Nuovo File
								<VisuallyHiddenInput
									type="file"
									onChange={(event) => {
										if (
											window.confirm(
												"Sei sicuro di voler caricare un nuovo file, l'operazione cancellerà irreversibilmente il file vecchio?"
											)
										)
											handleFileChange(doc.id, event);
									}}
								/>
							</Button>
						</Box>
						{doc.file && (
							<Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
								File selezionato: {doc.file.name}
							</Typography>
						)}
						{renderAdditionalInfo(doc, true)}
					</>
				);
			default:
				return null;
		}
	};

	const renderAdditionalInfo = (doc, editable) => (
		<>
			{(doc.file || doc.url) &&
				doc.additionalInformation.map((info) => (
					<React.Fragment key={info.id}>
						{(info.type === "text" || (info.type === "date" && activeTab === 0)) && (
							<TextField
								label={info.label || "Testo"}
								value={info.value}
								required={info.required}
								error={!!errors[doc.id]?.[info.id]}
								sx={{ marginTop: 1 }}
								helperText={errors[doc.id]?.[info.id]}
								onChange={(e) => handleAdditionalInfoChange(doc.id, info.id, e.target.value)}
								fullWidth
								disabled={!editable}
							/>
						)}
						{info.type === "number" && (
							<TextField
								label={info.label || "Numero"}
								type="number"
								value={info.value}
								required={info.required}
								sx={{ marginTop: 1 }}
								error={!!errors[doc.id]?.[info.id]}
								helperText={errors[doc.id]?.[info.id]}
								onChange={(e) => handleAdditionalInfoChange(doc.id, info.id, e.target.value)}
								fullWidth
								disabled={!editable}
							/>
						)}
						{info.type === "bool" && (
							<FormControlLabel
								sx={{ marginTop: 1 }}
								control={
									<Checkbox
										checked={info.value}
										onChange={(e) => handleAdditionalInfoChange(doc.id, info.id, e.target.checked)}
										disabled={!editable}
									/>
								}
								label={info.label || "Booleano"}
							/>
						)}
						{info.type === "date" && activeTab !== 0 && (
							<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
								<DatePicker
									label={info.label || "Data"}
									value={info.value ? dayjs(info.value, "DD-MM-YYYY") : null}
									sx={{ marginTop: 1 }}
									onChange={(newValue) => {
										console.log("newValue", newValue);
										handleAdditionalInfoChange(
											doc.id,
											info.id,
											newValue ? dayjs(newValue).format("DD-MM-YYYY") : null
										);
									}}
									slotProps={{
										textField: {
											fullWidth: true,
											error: !!errors[doc.id]?.[info.id],
											helperText: errors[doc.id]?.[info.id],
											disabled: !editable,
										},
									}}
								/>
							</LocalizationProvider>
						)}
					</React.Fragment>
				))}
		</>
	);

	const uploadedDocs = documents.filter((doc) => ["approved", "rejectedDone", "toApprove"].includes(doc.status));
	const rejectedDocs = documents.filter((doc) => doc.status === "rejected");
	const toUploadDocs = documents.filter((doc) => doc.status === "toUpload");
	const expiredDocs = documents.filter((doc) => doc.status === "expired");
	console.log("documents", documents);

	return authContext?.hasAuth("UPSERT_DOCUMENTS_SUPPLIER") ? (
		<Box sx={{ width: "100%" }}>
			<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
				<Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} aria-label="document tabs">
					<Tab label="Caricati" />
					<Tab
						label={
							<>
								<label>Da rettificare</label>
								{rejectedDocs.length > 0 && (
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											borderRadius: "50%",
											backgroundColor: "#FF4C4C",
											color: "#FFFFFF",
											width: "24px",
											height: "24px",
											fontSize: "14px",
											fontWeight: "600",
											marginLeft: "8px",
											boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
										}}
									>
										{rejectedDocs.length}
									</div>
								)}
							</>
						}
						sx={rejectedDocs.length > 0 ? { color: "error.main" } : {}}
					/>
					<Tab
						label={
							<>
								<label>
									Da caricare{" "}
									<b>
										{toUploadDocs.filter((doc) => doc.required === true).length > 0
											? `${toUploadDocs.filter((doc) => doc.required === true).length} ${toUploadDocs.filter((doc) => doc.required === true).length > 1 ? "necessari" : "necessario"}`
											: ""}
									</b>
									{toUploadDocs.filter((doc) => doc.required === false).length > 0
										? toUploadDocs.filter((doc) => doc.required === false).length > 1
											? " " +
												toUploadDocs.filter((doc) => doc.required === false).length +
												" opzionali"
											: " " +
												toUploadDocs.filter((doc) => doc.required === false).length +
												" opzionale"
										: ""}
								</label>
								{/*toUploadDocs.length > 0 &&
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      whiteSpace: 'nowrap',
                      borderRadius: '16px',
                      backgroundColor: '#3f51b5', // Blu più moderno
                      color: '#FFFFFF',
                      padding: '4px 12px', // Più spazio interno per migliorare leggibilità
                      fontSize: '12px',
                      fontWeight: '500',
                      marginLeft: '10px',
                      textTransform: 'uppercase',
                      height: 'auto',
                      lineHeight: '1.5',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      transition: 'background-color 0.3s ease', // Animazione per hover
                    }}
                  >
                    {toUploadDocs.filter(doc => doc.required === true).length} - obbligatorio
                  </div>
                */}
							</>
						}
					/>
					<Tab
						label={
							<>
								<label>Scaduti</label>
								{expiredDocs.length > 0 && (
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											borderRadius: "50%",
											backgroundColor: "#FF4C4C",
											color: "#FFFFFF",
											width: "24px",
											height: "24px",
											fontSize: "14px",
											fontWeight: "600",
											marginLeft: "8px",
											boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
										}}
									>
										{expiredDocs.length}
									</div>
								)}
							</>
						}
						sx={expiredDocs.length > 0 ? { color: "error.main" } : {}}
					/>
				</Tabs>
			</Box>
			<Box sx={{ padding: 2, display: "flex", flexDirection: "column", gap: 3 }}>
				{activeTab === 0 &&
					(uploadedDocs.length > 0 ? (
						uploadedDocs.map((doc) => (
							<Paper
								key={doc.id}
								elevation={3}
								sx={{
									padding: 3,
									borderRadius: 2,
								}}
							>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										marginBottom: 2,
									}}
								>
									<Typography variant="h6">
										{doc.label} {doc.required ? " - Necessario" : " - Opzionale"}
									</Typography>
								</Box>
								{renderDocumentContent(doc)}
								{(doc.status === "toApprove" ||
									doc.status === "approved" ||
									doc.status === "rejectedDone") && (
									<Box sx={{ marginTop: 2 }}>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												marginBottom: 1,
												width: "280px",
											}}
										>
											<Typography variant="subtitle1" gutterBottom>
												Informazioni aggiuntive
											</Typography>
											<IconButton
												sx={{ position: "relative", top: "-4px", left: "-30px" }}
												onClick={() => setViewingInfo(viewingInfo === doc.id ? null : doc.id)}
											>
												{viewingInfo === doc.id ? (
													<VisibilityOffIcon color={"black"} />
												) : (
													<VisibilityIcon color={"black"} />
												)}
											</IconButton>
										</Box>
										{viewingInfo === doc.id && renderAdditionalInfo(doc, false)}
									</Box>
								)}
							</Paper>
						))
					) : (
						<Typography variant="body1" sx={{ textAlign: "center", color: "text.secondary" }}>
							Nessun documento caricato da visualizzare.
						</Typography>
					))}
				{activeTab === 1 &&
					(rejectedDocs.length > 0 ? (
						rejectedDocs.map((doc) => (
							<Paper
								key={doc.id}
								elevation={3}
								sx={{
									padding: 3,
									borderRadius: 2,
								}}
							>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										marginBottom: 2,
									}}
								>
									<Typography variant="h6">
										{doc.label} {doc.required ? " - Necessario" : " - Opzionale"}
									</Typography>
								</Box>
								{renderDocumentContent(doc)}
							</Paper>
						))
					) : (
						<Typography variant="body1" sx={{ textAlign: "center", color: "text.secondary" }}>
							Nessun documento da rettificare.
						</Typography>
					))}
				{activeTab === 2 &&
					(toUploadDocs.length > 0 ? (
						toUploadDocs.map((doc) => (
							<Paper
								key={doc.id}
								elevation={3}
								sx={{
									padding: 3,
									borderRadius: 2,
								}}
							>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										marginBottom: 2,
									}}
								>
									<Typography variant="h6">
										{doc.label} {doc.required ? " - Necessario" : " - Opzionale"}
									</Typography>
								</Box>
								{renderDocumentContent(doc)}
							</Paper>
						))
					) : (
						<Typography variant="body1" sx={{ textAlign: "center", color: "text.secondary" }}>
							Nessun documento da caricare.
						</Typography>
					))}
				{activeTab === 2 && toUploadDocs.length > 0 && (
					<center>
						<Button
							variant="contained"
							color="secondary"
							onClick={handleSave}
							sx={{
								backgroundColor: "#333232",
								color: "white!important",
								mt: 2,
								width: "220px",
								height: "50px",
								fontSize: "16px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "8px",
							}}
						>
							Salva
						</Button>
					</center>
				)}
				{activeTab === 1 && rejectedDocs.length > 0 && (
					<center>
						<Button
							variant="contained"
							color="secondary"
							onClick={handleSave}
							sx={{
								backgroundColor: "#333232",
								color: "white!important",
								mt: 2,
								width: "220px",
								height: "50px",
								fontSize: "16px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "8px",
							}}
						>
							Salva
						</Button>
					</center>
				)}
				{activeTab === 3 &&
					(expiredDocs.length > 0 ? (
						expiredDocs.map((doc) => (
							<Paper
								key={doc.id}
								elevation={3}
								sx={{
									padding: 3,
									borderRadius: 2,
								}}
							>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										marginBottom: 2,
									}}
								>
									<Typography variant="h6">
										{doc.label} {doc.required ? " - Necessario" : " - Opzionale"}
									</Typography>
								</Box>
								{renderDocumentContent(doc)}
							</Paper>
						))
					) : (
						<Typography variant="body1" sx={{ textAlign: "center", color: "text.secondary" }}>
							Nessun documento scaduto.
						</Typography>
					))}
				{activeTab === 3 && expiredDocs.length > 0 && (
					<center>
						<Button
							variant="contained"
							color="secondary"
							onClick={handleSave}
							sx={{
								backgroundColor: "#333232",
								color: "white!important",
								mt: 2,
								width: "220px",
								height: "50px",
								fontSize: "16px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "8px",
							}}
						>
							Salva
						</Button>
					</center>
				)}
			</Box>
			<Snackbar
				open={showErrorSnackbar}
				autoHideDuration={6000}
				onClose={() => setShowErrorSnackbar(false)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				sx={{
					marginBottom: "160px",
				}}
			>
				<Alert onClose={() => setShowErrorSnackbar(false)} severity="error" sx={{ width: "100%" }}>
					Si prega di compilare tutti i campi necessari prima di continuare.
				</Alert>
			</Snackbar>
		</Box>
	) : (
		<MDBox mt={8}>
			<MDAlert color="error">Non hai i permessi per visualizzare questa pagina</MDAlert>
		</MDBox>
	);
};

export default UploadComponent;
